import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CornerDownLeft, Search } from 'lucide-react';
import { get } from '../lib/api';
import { cls } from '../lib/format';
import { NavIcon } from './Layout';
import { IotDot } from './ui';

// ⌘K / Ctrl+K command palette (ux-overhaul-plan §3.1): jump to any page, run common actions,
// and find assets by serial, plate or IMEI.

export type PaletteItem = { to: string; label: string; icon: string; group: 'Go to' | 'Actions'; keywords?: string };

export function CommandPalette({ open, onClose, items }: { open: boolean; onClose: () => void; items: PaletteItem[] }) {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sel, setSel] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (open) { setQ(''); setSel(0); } }, [open]);
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 160); return () => clearTimeout(t); }, [q]);
  const assets = useQuery({
    queryKey: ['palette-assets', debounced],
    queryFn: () => get<{ items: Array<{ id: string; serial: string; number: number; plateNo: string | null; iotState?: string; brand?: string; typeName?: string }> }>(`/v1/assets?limit=6&q=${encodeURIComponent(debounced)}`),
    enabled: open && debounced.length >= 2,
    staleTime: 10_000,
  });
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    const match = (i: PaletteItem) => !s || `${i.label} ${i.keywords ?? ''}`.toLowerCase().includes(s);
    const out: Array<{ key: string; group: string; label: string; hint?: string; icon?: string; iot?: string; to: string }> = [];
    items.filter((i) => i.group === 'Actions' && match(i)).forEach((i) => out.push({ key: i.to, group: 'Actions', label: i.label, icon: i.icon, to: i.to }));
    (debounced.length >= 2 ? assets.data?.items ?? [] : []).forEach((a) => out.push({ key: a.id, group: 'Assets', label: a.serial, hint: [a.brand, a.typeName].filter(Boolean).join(' ') || `#${a.number}`, iot: a.iotState, to: `/assets?id=${a.id}&pane=overview` }));
    items.filter((i) => i.group === 'Go to' && match(i)).slice(0, s ? 8 : 12).forEach((i) => out.push({ key: i.to, group: 'Go to', label: i.label, icon: i.icon, to: i.to }));
    return out;
  }, [q, debounced, items, assets.data]);
  useEffect(() => setSel(0), [q, results.length]);
  useEffect(() => { listRef.current?.querySelector(`[data-i="${sel}"]`)?.scrollIntoView({ block: 'nearest' }); }, [sel]);
  if (!open) return null;
  const go = (to: string) => { onClose(); nav(to); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(results.length - 1, s + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    else if (e.key === 'Enter' && results[sel]) { e.preventDefault(); go(results[sel]!.to); }
    else if (e.key === 'Escape') onClose();
  };
  let lastGroup = '';
  return (
    <div className="anim-fade fixed inset-0 z-[60] flex items-start justify-center bg-ink-950/50 p-4 pt-[12vh] backdrop-blur-[2px]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal aria-label="Command palette" className="anim-in w-full max-w-xl overflow-hidden rounded-2xl border border-line bg-surface shadow-an-lg">
        <div className="flex items-center gap-3 border-b border-line px-4">
          <Search className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Search assets, pages and actions…" aria-label="Search" role="combobox" aria-expanded aria-controls="palette-list" aria-activedescendant={`pal-${sel}`} className="h-14 w-full bg-transparent text-[15px] text-fg placeholder:text-fg-subtle focus:outline-none" />
          <kbd className="hidden rounded-md border border-line px-1.5 py-0.5 font-mono text-[10px] text-fg-subtle sm:block">ESC</kbd>
        </div>
        <div ref={listRef} id="palette-list" role="listbox" className="max-h-[50vh] overflow-y-auto p-2">
          {results.length === 0 && <p className="px-3 py-8 text-center text-sm text-fg-muted">{assets.isFetching ? 'Searching…' : 'No matches.'}</p>}
          {results.map((r, i) => {
            const header = r.group !== lastGroup ? ((lastGroup = r.group), <div key={`h-${r.group}`} className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">{r.group}</div>) : null;
            return (
              <div key={r.key + r.group}>
                {header}
                <button id={`pal-${i}`} data-i={i} role="option" aria-selected={i === sel} onMouseMove={() => setSel(i)} onClick={() => go(r.to)} className={cls('flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition', i === sel ? 'bg-brand-500/10 text-fg' : 'text-fg/85')}>
                  <span className={cls('grid h-7 w-7 shrink-0 place-items-center rounded-md', i === sel ? 'bg-brand-500/15 text-accent' : 'bg-fg/[0.05] text-fg-muted')}>
                    {r.icon ? <NavIcon name={r.icon} className="h-4 w-4" /> : <IotDot state={r.iot ?? 'no_device'} />}
                  </span>
                  <span className={cls('flex-1 truncate', r.group === 'Assets' && 'font-mono')}>{r.label}</span>
                  {r.hint && <span className="text-xs text-fg-subtle">{r.hint}</span>}
                  {i === sel ? <CornerDownLeft className="h-3.5 w-3.5 text-fg-subtle" aria-hidden /> : <ArrowRight className="h-3.5 w-3.5 text-transparent" aria-hidden />}
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 border-t border-line bg-bg/60 px-4 py-2 text-[11px] text-fg-subtle">
          <span><kbd className="font-mono">↑↓</kbd> navigate</span><span><kbd className="font-mono">↵</kbd> open</span><span className="ml-auto">Tip: type a serial, plate or IMEI</span>
        </div>
      </div>
    </div>
  );
}
